import React from "react";
import {
  Box,
  Typography,
  Button,
  makeStyles,
  Chip,
  Link
} from "@material-ui/core";
import { Link as RouterLink } from "react-router-dom";
import { CheckCircle, PlusCircle } from "../../icons/pricingListIcons";
import clsx from "clsx";
import { remCalc } from "../../../utils/helpers";
import Skeleton from "@material-ui/lab/Skeleton";
import { LANDING_URL } from "../../data/constants";

const useStyles = makeStyles(theme => ({
  planWrapper: {
    width: remCalc(288),
    margin: theme.spacing(0, 1.5),
    position: "relative",
    border: "1px solid #eee",
    borderRadius: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    "& ul": {
      listStyle: "none",
      padding: 0,
      margin: 0,
      fontSize: "0.875rem",
      lineHeight: "1rem"
    },
    "& li": {
      display: "flex",
      alignItems: "center",
      margin: theme.spacing(1.5, 0)
    },
    "& svg": {
      marginRight: theme.spacing(1.5)
    }
  },
  planWrapperPopular: {
    border: "1px solid #ddd"
  },
  cardHeader: {
    backgroundColor: "#f7f7f7",
    padding: theme.spacing(3, 2),
    height: remCalc(258),
    boxSizing: "border-box",
    borderBottom: "1px solid #EEE",
    borderRadius: theme.spacing(1, 1, 0, 0)
  },
  cardHeaderPopular: {
    backgroundColor: "#ECEEFF"
  },

  selectedIdentifier: {
    fontWeight: 500,
    color: theme.palette.text.secondary,
    fontSize: "0.9375rem",
    lineHeight: "2.25rem",
    textAlign: "center"
  },
  cardBody: {
    boxSizing: "border-box",
    flexGrow: 1,
    padding: theme.spacing(1.5, 2, 0),
    borderRadius: theme.spacing(0, 0, 1, 1),
    paddingBottom: theme.spacing(3)
  },
  cardBodyPopular: {
    backgroundColor: "#F9F9F9"
  },
  planWrapperActive: {
    backgroundColor: "#ECEEFF"
  },
  cardHeaderActive: {
    backgroundColor: "#ECEEFF"
  },
  cardBodyActive: {
    backgroundColor: "#ECEEFF"
  },
  title: {
    fontSize: "1.125rem",
    lineHeight: "1.3125rem",
    fontWeight: 900,
    letterSpacing: "1px",
    marginBottom: theme.spacing(1)
  },
  subtitle: {
    fontSize: "0.875rem",
    lineHeight: "1rem",
    marginBottom: theme.spacing(3),
    fontWeight: 500,
    color: theme.palette.secondary.main
  },
  price: {
    fontSize: "1.875rem",
    lineHeight: "2.1875rem",
    marginBottom: theme.spacing(0.5),
    fontWeight: 900
  },
  priceText: {
    fontSize: "1.5rem",
    fontWeight: "normal"
  },
  priceInfo: {
    fontSize: "0.875rem",
    lineHeight: "1rem",
    marginBottom: theme.spacing(4)
  },
  routerLink: {
    display: "block",
    textDecoration: "none"
  },
  btn: {
    textTransform: "none",
    textDecoration: "none !important"
  },
  blackBtn: {
    backgroundColor: theme.palette.text.primary,
    "&:hover": {
      backgroundColor: "#222"
    }
  },
  chip: {
    fontSize: "0.75rem",
    fontWeight: 500,
    height: remCalc(28),
    color: "#fff",
    borderRadius: theme.spacing(0.5),
    position: "absolute",
    top: "-14px",
    letterSpacing: "0.5px"
  },
  chipMostPopular: {
    backgroundColor: "#07A35A"
  },
  chipCurrentPlan: {
    backgroundColor: "#1329FE"
  }
}));

export default function SinglePlan({
  active,
  popular,
  price,
  title,
  subtitle,
  checkCircleItems,
  subscriptionStatus,
  plusCircleItems,
  currencyLoaded,
  buttonText,
  plan
}) {
  const classes = useStyles();
  return (
    <Box
      className={clsx(classes.planWrapper, {
        [classes.planWrapperActive]: active,
        [classes.planWrapperPopular]: popular && !active,
        [classes.planWrapperPopular]: popular && !active
      })}
      data-cy={`plan-${plan ? plan.name : title.toLowerCase()}`}
    >
      {popular && !active && (
        <Box display="flex" justifyContent="center">
          <Chip
            label="Most popular"
            className={clsx(classes.chip, classes.chipMostPopular)}
          />
        </Box>
      )}
      {active && (
        <Box display="flex" justifyContent="center">
          <Chip
            label="Your current plan"
            className={clsx(classes.chip, classes.chipCurrentPlan)}
          />
        </Box>
      )}
      <div
        className={clsx(classes.cardHeader, {
          [classes.cardHeaderActive]: active,
          [classes.cardHeaderPopular]: popular && !active
        })}
      >
        <Typography className={classes.title} data-cy="planTitle">
          {title}
        </Typography>
        <Typography className={classes.subtitle}>{subtitle}</Typography>
        <Typography
          className={clsx(classes.price, { [classes.priceText]: !price })}
          data-cy="priceIndicator"
        >
          {!currencyLoaded ? (
            <Skeleton variant="text" />
          ) : (
            `${price || "Custom pricing"}`
          )}
        </Typography>
        <Typography className={classes.priceInfo}>
          {price ? "per vehicle per month" : "tailored to your business"}
        </Typography>
        {!active || ["trialing", "canceled"].includes(subscriptionStatus) ? (
          price ? (
            <RouterLink
              to={`/payment/${plan.link}`}
              className={classes.routerLink}
            >
              <Button
                color="primary"
                variant="contained"
                fullWidth
                disableElevation
                className={classes.btn}
                id = {`select-${title.replace(' ', '')}`}
              >
                {buttonText || "Select plan"}
              </Button>
            </RouterLink>
          ) : (
            <Button
              color="primary"
              variant="contained"
              fullWidth
              disableElevation
              component={Link}
              href={`${LANDING_URL}/contact/sales`}
              target="_blank"
              className={clsx(classes.btn, classes.blackBtn)}
            >
              Contact sales
            </Button>
          )
        ) : (
          <Typography className={classes.selectedIdentifier}>
            Your current plan
          </Typography>
        )}
      </div>
      <div
        className={clsx(classes.cardBody, {
          [classes.cardBodyActive]: active,
          [classes.cardBodyPopular]: popular && !active
        })}
      >
        <ul>
          {checkCircleItems.map(item => (
            <li key={item.text}>
              <CheckCircle active={active || popular} />{" "}
              {item.style === "bold" ? <b>{item.text}</b> : item.text}
            </li>
          ))}
          {plusCircleItems.map(item => (
            <li key={item.text}>
              <PlusCircle active={active || popular} /> {item.text}
            </li>
          ))}
        </ul>
      </div>
    </Box>
  );
}
